import urls from 'routes/urls';

export type BusinessType = 
  | 'restaurant' 
  | 'bar' 
  | 'coffee_shop'
  | 'wholesale'
  | 'distributor'
  | 'pharmacy' 
  | 'hospital' 
  | 'shoe' 
  | 'clothing' 
  | 'hardware' 
  | 'grocery' 
  | 'jewelry' 
  | 'hotel'
  | 'mobile_money'
  | 'salon'
  | 'barbershop'
  | 'real_estate'
  | 'spa'
  | 'tailoring'
  | 'photography'
  | 'car_rental'
  | 'freelancer'
  | 'retail' 
  | 'general';

/**
 * Normalizes the business type from user data into dedicated supported business categories.
 * Based on BusinessTypeEnum and business_category from the backend.
 */
export const getNormalizedBusinessType = (userData: any): BusinessType => {
  const user = userData?.user || userData;

  // Check explicit business name first: "Hostel", "Hotel", "Boutique", etc.
  const explicitBizName = String(
    userData?.business_type ||
    userData?.business_type_name ||
    userData?.shop?.business_type ||
    userData?.shop?.business_type_name ||
    user?.business_type ||
    user?.shop?.business_type ||
    localStorage.getItem('business_type') ||
    localStorage.getItem('shop_business_type') ||
    ''
  ).toLowerCase().trim();

  const isHotelOrHostel = 
    explicitBizName === 'hotel' ||
    explicitBizName.includes('hotel') ||
    explicitBizName.includes('hostel') ||
    explicitBizName.includes('hospitality') ||
    explicitBizName.includes('lodge') ||
    explicitBizName.includes('resort') ||
    explicitBizName.includes('motel') ||
    explicitBizName.includes('guest_house') ||
    explicitBizName.includes('guesthouse') ||
    explicitBizName.includes('auberge') ||
    explicitBizName.includes('hebergement') ||
    explicitBizName.includes('hébergement');

  if (isHotelOrHostel) {
    return 'hotel';
  }

  // 1. Primary check: Numeric business_type_id
  const businessTypeId = Number(
    userData?.business_type_id ||
    userData?.shop?.business_type_id ||
    user?.business_type_id ||
    user?.shop?.business_type_id ||
    userData?.business_type_details?.id ||
    user?.business_type_details?.id ||
    localStorage.getItem('business_type_id') ||
    0
  );

  if (businessTypeId === 8) return 'pharmacy';
  if (businessTypeId === 7) return 'bar';
  if (businessTypeId === 112) return 'coffee_shop';
  if (businessTypeId === 113) return 'wholesale';
  if (businessTypeId === 114) return 'distributor';
  if ([9, 111].includes(businessTypeId)) return 'restaurant';
  if (businessTypeId === 118) return 'mobile_money';
  if ([11, 12].includes(businessTypeId) && !isHotelOrHostel) return 'hospital';
  if (businessTypeId === 5) return 'hardware';
  if (businessTypeId === 115) return 'shoe';
  if (businessTypeId === 4) return 'clothing';
  if (businessTypeId === 3) return 'grocery';
  if (businessTypeId === 6) return 'jewelry';
  if (businessTypeId === 217) return 'salon';
  if (businessTypeId === 218) return 'barbershop';
  if (businessTypeId === 219) return 'real_estate';
  if (businessTypeId === 220) return 'spa';
  if (businessTypeId === 221) return 'tailoring';
  if (businessTypeId === 222) return 'car_rental';
  if (businessTypeId === 226) return 'photography';
  if (businessTypeId === 227) return 'freelancer';
  if (businessTypeId === 305) return 'hotel';
  if ([1, 2].includes(businessTypeId)) return 'retail';

  const isClothingSpecific = 
    explicitBizName.includes('clothing') || 
    explicitBizName.includes('garment') || 
    explicitBizName.includes('apparel') || 
    explicitBizName.includes('vetement') || 
    explicitBizName.includes('vêtement') || 
    explicitBizName.includes('tailor');

  if (!isClothingSpecific && (explicitBizName === 'boutique' || explicitBizName.includes('boutique'))) {
    return 'retail';
  }

  // 2. Secondary check: Specific business_type string or slug
  const rawType = 
    userData?.business_type_slug ||
    userData?.business_type_name ||
    (typeof userData?.business_type === 'string' ? userData?.business_type : '') ||
    userData?.shop?.business_type_slug ||
    userData?.shop?.business_type_name ||
    userData?.shop?.business_types?.name ||
    (typeof userData?.shop?.business_type === 'string' ? userData?.shop?.business_type : '') ||
    userData?.business_type_details?.slug ||
    userData?.business_type_details?.name ||
    user?.business_type_slug ||
    user?.business_type_name ||
    (typeof user?.business_type === 'string' ? user?.business_type : '') ||
    user?.shop?.business_type_slug ||
    user?.shop?.business_type_name ||
    user?.shop?.business_types?.name ||
    (typeof user?.shop?.business_type === 'string' ? user?.shop?.business_type : '') ||
    userData?.data?.business_type ||
    userData?.data?.shop?.business_types?.name ||
    localStorage.getItem('business_type') ||
    localStorage.getItem('shop_business_type') ||
    // ONLY check business_category as a fallback if business_type is absent
    (typeof userData?.shop?.business_category === 'string' && userData?.shop?.business_category !== 'general' ? userData?.shop?.business_category : '') ||
    (typeof userData?.business_category === 'string' && userData?.business_category !== 'general' ? userData?.business_category : '') ||
    (typeof user?.shop?.business_category === 'string' && user?.shop?.business_category !== 'general' ? user?.shop?.business_category : '') ||
    (typeof user?.business_category === 'string' && user?.business_category !== 'general' ? user?.business_category : '') ||
    (typeof userData?.shop?.business_category === 'string' ? userData?.shop?.business_category : '') ||
    (typeof userData?.business_category === 'string' ? userData?.business_category : '') ||
    localStorage.getItem('business_category') ||
    '';
  const type = String(rawType).toLowerCase().trim();

  // 1. Real Estate & Property
  if (
    type.includes('real_estate') ||
    type.includes('realestate') ||
    type.includes('real estate') ||
    type.includes('property') ||
    type.includes('immobilier') ||
    type.includes('logement')
  ) {
    return 'real_estate';
  }

  // 2. Barbershop & Men's Grooming
  if (type.includes('barber') || type.includes('barbier')) {
    return 'barbershop';
  }

  // 3. Spa & Wellness
  if (type.includes('spa') && !type.includes('salon_spa')) {
    return 'spa';
  }
  if (type.includes('massage') || type.includes('wellness') || type.includes('sauna')) {
    return 'spa';
  }

  // 4. Hair Salon & Beauty Studio
  if (
    type.includes('salon') ||
    type.includes('coiffure') ||
    type.includes('beauty') ||
    type.includes('hairstyling') ||
    type.includes('esthetic')
  ) {
    return 'salon';
  }

  // 5. Tailoring & Custom Couture
  if (
    type.includes('tailor') ||
    type.includes('couture') ||
    type.includes('couturier') ||
    type.includes('retouche')
  ) {
    return 'tailoring';
  }

  // 6. Car Rental & Fleet
  if (
    type.includes('car_rental') ||
    type.includes('car rental') ||
    type.includes('vehicle') ||
    type.includes('fleet') ||
    type.includes('location voiture')
  ) {
    return 'car_rental';
  }

  // 7. Photography & Media
  if (type.includes('photo') || type.includes('studio') || type.includes('camera')) {
    return 'photography';
  }

  // 8. Freelancer & Professional Services
  if (
    type.includes('freelanc') ||
    type.includes('consult') ||
    type.includes('agency') ||
    type.includes('cleaning') ||
    type.includes('professional') ||
    type.includes('services')
  ) {
    return 'freelancer';
  }

  // 9. Hotel & Hospitality PMS (Checked BEFORE hospital because "hospitality" contains "hospital")
  if (
    type === 'hotel' ||
    type.includes('hotel') ||
    type.includes('hospitality') ||
    type.includes('lodge') ||
    type.includes('resort') ||
    type.includes('motel') ||
    type.includes('hostel') ||
    type.includes('guest_house') ||
    type.includes('guesthouse') ||
    type.includes('auberge') ||
    type.includes('hebergement') ||
    type.includes('hébergement')
  ) {
    return 'hotel';
  }

  // 10. Hospital / Clinical
  if ((type.includes('hospital') && !type.includes('hospitality')) || type.includes('clinic') || type.includes('medical')) return 'hospital';
  
  // 11. Pharmacy
  if (type.includes('pharmacy') || type.includes('dispensary')) return 'pharmacy';

  // 12. Mobile Money & Agency Banking
  if (
    type.includes('mobile_money') ||
    type.includes('mobile money') ||
    type.includes('agency_banking') ||
    type.includes('agency banking') ||
    type.includes('momo') ||
    type.includes('airtime') ||
    type.includes('financier') ||
    type.includes('financial')
  ) {
    return 'mobile_money';
  }

  // 13. Shoe & Footwear
  if (type.includes('shoe') || type.includes('footwear') || type.includes('shoes')) return 'shoe';

  // 14. Clothing & Apparel (Specific clothing/garment/apparel/vetement stores)
  if (
    type.includes('clothing') ||
    type.includes('garment') ||
    type.includes('apparel') ||
    type.includes('vetement') ||
    type.includes('vêtement') ||
    type.includes('habille') ||
    type.includes('textile') ||
    type.includes('fashion')
  ) {
    return 'clothing';
  }

  // 15. Hardware & Building Supplies
  if (type.includes('hardware') || type.includes('tools') || type.includes('building')) return 'hardware';

  // 16. Grocery & Supermarket
  if (type.includes('grocery') || type.includes('supermarket')) return 'grocery';

  // 17. Jewelry & Luxury
  if (type.includes('jewelry') || type.includes('gem')) return 'jewelry';

  // 18. Wholesaler & FMCG B2B
  if (type.includes('wholesale') || type.includes('grossiste')) return 'wholesale';

  // 19. Regional Distributor & Supply Logistics
  if (type.includes('distribut')) return 'distributor';
  
  // 20. Coffee Shop, Roasters & Specialty Cafes
  if (
    type === 'coffee_shop' || 
    type === 'coffee shop' || 
    type === 'coffeeshop' ||
    type.includes('coffee') || 
    type.includes('roaster')
  ) {
    return 'coffee_shop';
  }

  // 21. Restaurant & Food & Beverage
  if (
    type === 'restaurant' || 
    type === 'cafe' || 
    type === 'bakery' || 
    type === 'food' ||
    type.includes('restaurant') || 
    (type.includes('cafe') && !type.includes('internet'))
  ) {
    return 'restaurant';
  }
  
  // 22. Bar & Nightlife
  if (
    type === 'bar' || 
    type === 'club' || 
    type === 'lounge' || 
    type === 'snack_bar' ||
    type === 'drinks' ||
    ['bar', 'club', 'lounge'].some(b => type.includes(b))
  ) {
    return 'bar';
  }

  // 23. Retail (Shop, Boutique, Store, Magasin, General Retail)
  return 'retail';
};

/**
 * Returns the correct dashboard URL based on the business type.
 * Ensures strict landing for all microservice business types.
 */
export const getDashboardPath = (userData: any): string => {
  const user = userData?.user || userData;
  
  // Super Admin bypass
  if (user?.is_super_admin || userData?.is_super_admin) return urls.superAdminDashboard;

  const businessType = getNormalizedBusinessType(userData);

  // Check whether the merchant is a physical / Pro shop
  const hasPhysicalBusinessType = Boolean(
    userData?.shop?.business_type_id ||
    userData?.business_type_id ||
    user?.shop?.business_type_id ||
    user?.business_type_id ||
    userData?.shop?.business_types?.id ||
    user?.shop?.business_types?.id ||
    Number(localStorage.getItem('business_type_id') || 0) > 0
  );

  // FUKKONI Lite Merchant bypass -> Direct to Online Store Hub!
  // ONLY if the merchant is explicitly Lite AND has NO physical Pro business type!
  const planType = (
    userData?.shop?.plan_type ||
    userData?.plan_type ||
    user?.shop?.plan_type ||
    user?.plan_type ||
    userData?.shop_plan_type ||
    localStorage.getItem('plan_type') ||
    ''
  ).toLowerCase();

  const isPureLiteMerchant = planType === 'lite' && !hasPhysicalBusinessType;

  if (isPureLiteMerchant) {
    return urls.merchantOnlineStore;
  }

  const hasPermission = (featureKey: number): boolean => {
    const roleName = (userData?.user_role_name || userData?.user_role || user?.user_role_name || user?.user_role || '').toLowerCase();
    const isExplicitAdmin = userData?.is_admin === true || user?.is_admin === true || userData?.user?.user_role_id === 1 || user?.user_role_id === 1;
    const isNamedAdmin = roleName.includes('administrator') || roleName.includes('owner');
    if (isExplicitAdmin || isNamedAdmin) return true;

    const permissions = userData?.permissions || user?.permissions || [];
    return permissions.includes(featureKey);
  };

  switch (businessType) {
    case 'real_estate':
      return urls.realEstateDashboard;
    case 'barbershop':
      return urls.barbershopDashboard;
    case 'spa':
      return urls.spaDashboard;
    case 'tailoring':
      return urls.tailoringDashboard;
    case 'freelancer':
      return urls.professionalServicesDashboard;
    case 'salon':
      return urls.salonDashboard;
    case 'car_rental':
      return urls.carRentalDashboard;
    case 'coffee_shop':
      return urls.coffeeDashboard;
    case 'wholesale':
      return urls.dashboard;
    case 'distributor':
      return urls.deliveryNotes;
    case 'mobile_money':
      return urls.mobileMoneyDashboard;
    case 'shoe':
      return urls.shoeDashboard;
    case 'clothing':
      return urls.clothingDashboard;
    case 'hardware':
      return urls.hardwareDashboard;
    case 'grocery':
      return urls.groceryDashboard;
    case 'jewelry':
      return urls.jewelryDashboard;
    case 'hotel':
      if (hasPermission(140)) return urls.hotelDashboard;
      if (hasPermission(141)) return urls.hotelRooms;
      if (hasPermission(142)) return urls.hotelReservations;
      if (hasPermission(143)) return urls.hotelHousekeeping;
      if (hasPermission(144)) return urls.hotelFolios;
      if (hasPermission(146)) return urls.hotelGuests;
      return urls.hotelDashboard;
    case 'restaurant':
      if (hasPermission(59)) return urls.restaurantDashboard;
      if (hasPermission(60)) return urls.restaurantTables;
      return urls.restaurantDashboard;
    case 'bar':
      if (hasPermission(37)) return urls.barDashboard;
      if (hasPermission(41)) return urls.barPOS;
      return urls.barDashboard;
    case 'pharmacy':
      if (hasPermission(35)) return urls.pharmacyDashboard;
      if (hasPermission(49)) return urls.pharmacyPOS;
      return urls.pharmacyDashboard;
    case 'hospital':
      if (hasPermission(80)) return urls.hospitalDashboard;
      if (hasPermission(81)) return urls.hospitalQueue;
      if (hasPermission(84)) return urls.hospitalHR;
      if (hasPermission(85)) return urls.hospitalPayroll;
      if (hasPermission(87)) return urls.hospitalWards;
      if (hasPermission(88)) return urls.hospitalLabs;
      if (hasPermission(86)) return urls.hospitalFinance;
      return urls.hospitalDashboard;
    case 'retail':
    default:
      if (hasPermission(1)) return urls.dashboard;
      if (hasPermission(31)) return urls.pos;
      if (hasPermission(3)) return urls.sales;
      return urls.dashboard;
  }
};

export const getPosPath = (userData: any): string => {
  const businessType = getNormalizedBusinessType(userData);
  switch (businessType) {
    case "coffee_shop": return urls.coffeePOS;
    case "bar": return urls.barPOS;
    case "pharmacy": return urls.pharmacyPOS;
    case "grocery": return urls.groceryPOS;
    case "shoe": return urls.shoePOS;
    case "jewelry": return urls.jewelryPOS;
    case "mobile_money": return urls.mobileMoneyPOS;
    case "wholesale": return urls.wholesale;
    case "distributor": return urls.wholesale;
    case "car_rental": return urls.carRentalAgreements;
    case "barbershop": return urls.barbershopPOS;
    case "salon": return urls.salonAppointments;
    case "spa": return urls.spaBookings;
    default: return urls.pos;
  }
};

export const getStockPath = (userData: any): string => {
  const businessType = getNormalizedBusinessType(userData);
  switch (businessType) {
    case "bar": return urls.barStock;
    case "pharmacy": return urls.medications;
    case "grocery": return urls.groceryInventory;
    case "shoe": return urls.shoeCatalog;
    case "jewelry": return urls.jewelryVault;
    case "hardware": return urls.hardwareCatalog;
    case "tailoring": return urls.tailoringFabrics;
    case "car_rental": return urls.carRentalVehicles;
    case "real_estate": return urls.realEstateUnits;
    default: return urls.products;
  }
};
